"use client";

import { PortableText } from "@portabletext/react";
import {
  customRichTextComponent,
  richTextComponent,
} from "@/sanity/sanityPortableText";
import { CTAButton } from "@/shadcn/ui/button";
import { GroqImage } from "@/helpers/Image";
import type { PageAffiliateSchema } from "@/sanity/pages/PageAffiliateTemplate";
import Image from "next/image";
import logo from "@/public/logo-black.svg";
import arrow from "@/public/global/dotted-arrow.svg";
import { motion } from "framer-motion";
import { Fragment } from "react";
import Link from "next/link";
import { cn } from "@/lib/utils";
import bg from "@/public/global/bg-art.jpg";
import ChipSerif from "@/components/global/ui/ChipSerif";
import coopBG from "@/public/affiliate/cooperationbg.png";
import { useIsMobile } from "@/hooks/useIsMobile";

export default function Hero({
  data,
  className,
}: {
  data: PageAffiliateSchema;
  className?: string;
}) {
  const isMobile = useIsMobile();
  return (
    <>
      <section
        id="hero-section"
        className={cn(
          "relative overflow-clip px-[2rem] lg:px-[5rem] pt-[2rem] lg:pt-[5rem]",
          className,
        )}
      >
        <div className="relative overflow-clip w-[min(229.25rem,100%)] mx-auto rounded-[2rem] lg:rounded-[5rem] pt-[5rem] pb-[3.25rem] lg:pb-[4.5rem]">
          <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-b from-[#F6F9F7] to-[#E6F2EB]">
            <Image
              className="absolute left-1/2 -translate-x-1/2 bottom-0 !w-[max(100%,90rem)] !max-w-none !h-full object-cover object-top mix-blend-luminosity opacity-[.1]"
              src={bg}
              quality={100}
              alt="background art"
            />
          </div>

          <div className="container flex relative z-10 flex-col items-center px-[1.5rem]">
            <motion.div
              className="flex justify-center items-center space-x-[1rem]"
              initial={{ opacity: 0, filter: "blur(10px)" }}
              animate={{ opacity: 1, filter: "blur(0px)" }}
              transition={{
                duration: 0.3,
                delay: 0,
                ease: [0.25, 0.4, 0.25, 1],
              }}
            >
              <Link href={"/"}>
                <Image src={logo} alt="" className="w-[16.25rem]" />
              </Link>

              {data?.heroPartner && (
                <Fragment>
                  <Image src={arrow} alt="" className="shrink-0" />
                  <div className="flex items-center gap-[1rem]">
                    <div className="rounded-full w-[4.5rem] h-[4.5rem] overflow-clip shrink-0">
                      <Image
                        src={data?.heroPartner.photo}
                        alt=""
                        className="shrink-0"
                      />
                    </div>
                    <p className="text-[#2E3436] text-[2.25rem] font-[500] leading-[116%] m-0 w-min lg:w-auto">
                      {data?.heroPartner.text}
                    </p>
                  </div>
                </Fragment>
              )}
            </motion.div>
            <div className="flex flex-col justify-between items-center w-[min(166.25rem,100%)] lg:flex-row mt-[2rem] lg:mt-[7.5rem]">
              <motion.div
                className="w-[min(64.75rem,100%)] space-y-[1.5rem] mb-[4.5rem] lg:mb-0"
                initial={{ opacity: 0, x: -50, filter: "blur(10px)" }}
                animate={{ opacity: 1, x: 0, filter: "blur(0px)" }}
                transition={{
                  duration: 0.3,
                  delay: 0.05,
                  ease: [0.25, 0.4, 0.25, 1],
                }}
              >
                <div className="flex items-center justify-center lg:justify-start gap-[1rem] lg:gap-[1.75rem]">
                  {data?.heroChip1 && (
                    <ChipSerif text={data?.heroChip1.text} className="mb-0" />
                  )}
                  <div className="w-[0.5rem] h-[0.5rem] shrink-0 bg-[#6DA991]" />
                  {data?.heroChip2 && (
                    <ChipSerif text={data?.heroChip2.text} className="mb-0" />
                  )}
                </div>

                <div>
                  {data?.heroText && (
                    <div className="richtext lg:richtext-start prose-h1:text-[#2E3436] prose-h1:text-[4rem] lg:prose-h1:text-[6rem] prose-h1:leading-[110%] mb-[2.25rem] lg:mb-[1.5rem]">
                      <PortableText
                        value={data.heroText}
                        components={richTextComponent}
                      />
                    </div>
                  )}

                  {data?.heroContent && (
                    <div className="richtext richtext-center lg:richtext-start prose-p:text-[2rem] prose-p:leading-[150%] prose-p:font-[400] prose-strong:font-[400] prose-strong:text-black prose-p:text-[#1C1F22]/70">
                      <PortableText
                        value={data.heroContent}
                        components={richTextComponent}
                      />
                    </div>
                  )}
                </div>

                <div className="w-full">
                  <CTAButton data={data.heroCTA} className="w-full" />
                </div>
              </motion.div>
              <div className="w-[min(73.375rem,100%)] relative lg:h-[63.375rem] ">
                {data?.heroImage && (
                  <motion.div
                    initial={{ opacity: 0, y: 40, filter: "blur(10px)" }}
                    animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                    transition={{
                      duration: 0.3,
                      delay: 0.2,
                      ease: [0.25, 0.4, 0.25, 1],
                    }}
                    className="w-full lg:h-full overflow-clip rounded-[2rem]"
                  >
                    <Image
                      src={data?.heroImage}
                      alt=""
                      width={1000}
                      height={1000}
                      quality={100}
                      className="object-center w-full"
                    />
                  </motion.div>
                )}

                {data.heroImageBadge1 && (
                  <motion.div
                    initial={{ opacity: 0, x: -120, filter: "blur(10px)" }}
                    animate={{
                      opacity: 1,
                      x: isMobile ? 0 : -70,
                      filter: "blur(0px)",
                    }}
                    transition={{
                      duration: 0.3,
                      delay: 0.35,
                      ease: [0.25, 0.4, 0.25, 1],
                    }}
                    className="absolute -translate-x-[30%] left-[2rem] bottom-[2.25rem] lg:bottom-[65%] z-[2] w-[18.875rem] lg:w-[27.5rem] shadow-[0_-0.911px_3.642px_0_rgba(138,_86,_22,_0.10)]"
                  >
                    <Image
                      src={data.heroImageBadge1}
                      alt=""
                      quality={100}
                      className="rounded-[0.5rem] lg:rounded-[0.68rem]"
                    />
                  </motion.div>
                )}

                {data.heroImageBadge2 && (
                  <motion.div
                    initial={{ opacity: 0, x: 120, filter: "blur(10px)" }}
                    animate={{
                      opacity: 1,
                      x: isMobile ? -56 : 45,
                      filter: "blur(0px)",
                    }}
                    transition={{
                      duration: 0.3,
                      delay: 0.4,
                      ease: [0.25, 0.4, 0.25, 1],
                    }}
                    className="absolute right-0 bottom-[2.25rem] lg:bottom-[30%] translate-x-[30%] z-[2] w-[17.5rem] lg:w-[23.1875rem]"
                  >
                    <Image
                      src={data.heroImageBadge2}
                      alt=""
                      quality={100}
                      className="rounded-[0.5rem] lg:rounded-[0.68rem]"
                    />
                  </motion.div>
                )}
              </div>
            </div>
            <motion.div
              className="relative mt-[2.25rem] lg:mt-[6.5rem] w-[min(166.25rem,100%)] mx-auto rounded-[1.5rem] lg:rounded-[3rem] px-[3rem] lg:px-[7.5rem] pt-[3.88rem] pb-[5rem] lg:py-[5.75rem] bg-[#103037] overflow-clip flex flex-col lg:flex-row justify-between gap-[4.5rem] items-center"
              initial={{ opacity: 0, y: 40, filter: "blur(10px)" }}
              animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
              transition={{
                duration: 0.3,
                delay: 0.1,
                ease: [0.25, 0.4, 0.25, 1],
              }}
            >
              <Image
                src={coopBG}
                alt=""
                className="absolute z-0 right-0 bottom-0 h-full lg:max-w-[82.25rem] object-cover"
              />
              {data?.heroCallout && (
                <div className="richtext richtext-center lg:richtext-start prose-p:text-[2rem] prose-p:leading-[150%] prose-p:font-[400] prose-strong:font-[400] prose-strong:text-white prose-p:text-white/70 lg:max-w-[72.35875rem]">
                  <PortableText
                    value={data.heroCallout}
                    components={richTextComponent}
                  />
                </div>
              )}
              <div className="flex justify-center items-center gap-[2rem] max-w-[38.5rem] lg:max-w-none">
                <p className="m-0 text-white/50 text-[1.75rem] lg:text-[2rem] font-[400] shrink-0">
                  {data.heroCooperation ?? "In Kooperation mit"}
                </p>

                {data?.heroPartner && (
                  <div className="flex items-center rounded-full gap-[1rem] w-fit">
                    <div className="rounded-full w-[4.5rem] h-[4.5rem] overflow-clip shrink-0">
                      {data?.heroPartner.photo && (
                        <Image
                          src={data?.heroPartner.photo}
                          alt=""
                          className="shrink-0"
                        />
                      )}
                    </div>
                    <p className="text-white text-[2rem] font-[600] leading-[116%] m-0">
                      {data?.heroPartner.text}
                    </p>
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </>
  );
}
